import { useState, useEffect, useCallback } from 'react';
import api, { urlDocument } from 'services/api';
import { apercuDocument } from 'services/documents';
import BoutonTelechargerDocument from 'components/shared/BoutonTelechargerDocument';
import useEscapeClose from 'hooks/useEscapeClose';



function expirationBadge(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return <span className="gss-badge" style={{ background: 'rgba(220,38,38,0.08)', color: '#DC2626', fontSize: '0.65rem' }}>Expire</span>;
  if (diff <= 30) return <span className="gss-badge" style={{ background: 'rgba(220,38,38,0.08)', color: '#DC2626', fontSize: '0.65rem' }}>&lt; 30j</span>;
  if (diff <= 60) return <span className="gss-badge" style={{ background: 'rgba(245,158,11,0.08)', color: '#D97706', fontSize: '0.65rem' }}>&lt; 60j</span>;
  return null;
}

const EMPTY = {
  client_id: '', intitule: 'Surveillance et Gardiennage de Site',
  date_debut: '', date_fin: '', duree_mois: 12,
  tarif_agent: 0, contact_client: '', titre_client: '', telephone_client: '',
  sites_details: [], conditions: '', supervision_incluse: false, offre_id: '',
};

export default function ContratPrestationPage() {
  const [contrats, setContrats] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSites, setClientSites] = useState([]);
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEscapeClose(showWizard, () => setShowWizard(false));

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cl, of] = await Promise.all([
        api.get('/contrats-prestation'),
        api.get('/clients'),
        api.get('/offres').catch(() => ({ data: [] })),
      ]);
      setContrats(c.data);
      setClients(cl.data);
      setOffres(of.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Load sites when client changes
  const onClientChange = async (clientId) => {
    set('client_id', clientId);
    if (clientId) {
      try {
        const res = await api.get('/sites', { params: { client_id: clientId } });
        setClientSites(res.data);
        // Auto-fill contact info
        const cl = clients.find(c => c.id === clientId);
        if (cl) {
          set('contact_client', cl.nom_contact || '');
          set('telephone_client', cl.telephone || '');
        }
      } catch { setClientSites([]); }
    } else {
      setClientSites([]);
    }
  };

  const onOffreChange = (offreId) => {
    set('offre_id', offreId);
    if (offreId) {
      const offre = offres.find(o => o.id === offreId);
      if (offre) {
        set('tarif_agent', Number(offre.tarif_agent_mois) || 0);
        const services = typeof offre.services === 'string' ? JSON.parse(offre.services) : (offre.services || {});
        set('supervision_incluse', !!services.supervision_physique);
      }
    } else {
      set('tarif_agent', 0);
      set('supervision_incluse', false);
    }
  };

  const selectedOffre = offres.find(o => o.id === form.offre_id);
  const selectedOffreServices = (() => {
    if (!selectedOffre) return {};
    try { return typeof selectedOffre.services === 'string' ? JSON.parse(selectedOffre.services) : (selectedOffre.services || {}); } catch { return {}; }
  })();

  const addSite = (site) => {
    set('sites_details', [...form.sites_details, { site_id: site.id, nom: site.nom, adresse: site.adresse || '', agents_jour: 1, agents_nuit: 1 }]);
  };

  const removeSite = (idx) => {
    set('sites_details', form.sites_details.filter((_, i) => i !== idx));
  };

  const updateSite = (idx, field, val) => {
    const sites = [...form.sites_details];
    sites[idx] = { ...sites[idx], [field]: val };
    set('sites_details', sites);
  };

  const totalAgents = form.sites_details.reduce((s, site) => s + (site.agents_jour || 0) + (site.agents_nuit || 0), 0);
  const montantMensuel = totalAgents * (form.tarif_agent || 0);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await api.post('/contrats-prestation/generer', form);
      await apercuDocument(urlDocument('contrat-prestation', res.data.id, 'pdf'));
      setShowWizard(false);
      setForm(EMPTY);
      setStep(1);
      loadAll();
    } catch (err) {
      const data = err.response?.data;
      /* ⚠️ ON SE BRANCHE SUR LE `code`, PLUS SUR `error` — 18/10/2026. Le
       *    serveur rend desormais une PHRASE dans `error` (les 92 ecrans qui
       *    l affichent lisaient « champs_manquants » en clair). Le slug vit
       *    dans `code` : *un identifiant pour la machine, une phrase pour
       *    l humain, et jamais l un a la place de l autre.* */
      if (data?.code === 'CHAMPS_MANQUANTS' && data.champs) {
        setError(`Impossible de générer le contrat. Champs à renseigner : ${data.champs.join(', ')}`);
      } else {
        setError(data?.error || err.message || 'Erreur de génération');
      }
    } finally { setGenerating(false); }
  };

  const stepTitles = ['Type & Client', 'Sites', 'Effectifs', 'Tarification', 'Apercu'];
  const availableSites = clientSites.filter(s => !form.sites_details.some(d => d.site_id === s.id));

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Contrats</span></div>
          <div className="stat-card-value">{contrats.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Actifs</span></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{contrats.filter(c => c.statut === 'actif').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Montant mensuel total</span></div>
          <div className="stat-card-value">{contrats.reduce((s, c) => s + Number(c.montant_mensuel || 0), 0).toLocaleString()} F</div>
        </div>
      </div>

      {error && <div className="gss-alert gss-alert-error">{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button></div>}

      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title">Contrats de prestation</div>
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setStep(1); setShowWizard(true); }}>+ Nouveau contrat</button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : contrats.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDD" strokeWidth="1.5" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666' }}>Aucun contrat de prestation</div>
          </div>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr><th>Numero</th><th>Client</th><th>Sites</th><th>Agents</th><th>Montant/mois</th><th>Debut</th><th>Statut</th><th>PDF</th></tr>
              </thead>
              <tbody>
                {contrats.map(c => (
                  <tr key={c.id}>
                    <td><strong>{c.numero}</strong></td>
                    <td>{c.client_nom || '-'}</td>
                    <td>{c.nb_sites}</td>
                    <td>{c.nb_agents}</td>
                    <td style={{ fontWeight: 600 }}>{Number(c.montant_mensuel || 0).toLocaleString()} F</td>
                    <td style={{ fontSize: '0.78rem' }}>{c.date_debut?.split('T')[0]}</td>
                    <td>
                      <span className={`gss-badge ${c.statut === 'actif' ? 'gss-badge-green' : 'gss-badge-grey'}`}>{c.statut}</span>
                      {' '}{expirationBadge(c.date_fin)}
                    </td>
                    <td>
                      {c.document_url && <button className="gss-btn-sm" onClick={() => apercuDocument(urlDocument('contrat-prestation', c.id, 'pdf'))}>PDF</button>}
                      {c.document_url && <BoutonTelechargerDocument url={urlDocument('contrat-prestation', c.id, 'pdf')} format="PDF" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══ WIZARD MODAL ═══ */}
      {showWizard && (
        <div className="gss-modal-overlay" onClick={() => setShowWizard(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="gss-modal-header">
              <div>
                <h2>Nouveau contrat de prestation</h2>
                <div className="gss-modal-subtitle">Etape {step} / 5 — {stepTitles[step - 1]}</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowWizard(false)}>&times;</button>
            </div>

            {/* Progress */}
            <div style={{ display: 'flex', gap: 4, padding: '0 1.5rem' }}>
              {stepTitles.map((t, i) => (
                <div key={i} style={{ flex: 1 }}>
                  <div style={{ height: 4, borderRadius: 2, background: i + 1 <= step ? '#1B3A5C' : '#E0E0E0' }} />
                  <div style={{ fontSize: 9, marginTop: 2, color: i + 1 === step ? '#1B3A5C' : '#999', fontWeight: i + 1 === step ? 700 : 400 }}>{t}</div>
                </div>
              ))}
            </div>

            <div style={{ padding: '1.5rem', minHeight: 300 }}>
              {/* STEP 1: Client */}
              {step === 1 && (
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label>Client *</label>
                    <select className="gss-input" value={form.client_id} onChange={e => onClientChange(e.target.value)}>
                      <option value="">Selectionner un client</option>
                      {clients.filter(c => c.actif).map(c => <option key={c.id} value={c.id}>{c.raison_sociale} — [{c.statut_commercial}]</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Contact client</label><input className="gss-input" value={form.contact_client} onChange={e => set('contact_client', e.target.value)} /></div>
                  <div className="gss-field"><label>Titre / Fonction</label><input className="gss-input" value={form.titre_client} onChange={e => set('titre_client', e.target.value)} placeholder="Directeur General" /></div>
                  <div className="gss-field"><label>Telephone</label><input className="gss-input" value={form.telephone_client} onChange={e => set('telephone_client', e.target.value)} /></div>
                  <div className="gss-field gss-field-full"><label>Intitule</label><input className="gss-input" value={form.intitule} onChange={e => set('intitule', e.target.value)} /></div>
                  <div className="gss-field"><label>Date debut *</label><input className="gss-input" type="date" value={form.date_debut} onChange={e => set('date_debut', e.target.value)} /></div>
                  <div className="gss-field"><label>Duree (mois)</label><input className="gss-input" type="number" min="1" value={form.duree_mois} onChange={e => set('duree_mois', parseInt(e.target.value) || 12)} /></div>
                  <div className="gss-field gss-field-full">
                    <label>Offre commerciale</label>
                    <select className="gss-input" value={form.offre_id} onChange={e => onOffreChange(e.target.value)}>
                      <option value="">-- Selectionner une offre --</option>
                      {offres.map(o => <option key={o.id} value={o.id}>{o.nom} — {Number(o.tarif_agent_mois).toLocaleString()} FCFA/agent/mois</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* STEP 2: Sites */}
              {step === 2 && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>Ajouter des sites du client</label>
                    {availableSites.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {availableSites.map(s => (
                          <button key={s.id} className="gss-btn-sm" onClick={() => addSite(s)} style={{ fontSize: 12 }}>+ {s.nom}</button>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{form.client_id ? 'Tous les sites ont ete ajoutes' : 'Selectionnez un client a l\'etape 1'}</p>
                    )}
                  </div>
                  {form.sites_details.length > 0 && (
                    <div className="gss-table-wrap">
                      <table className="gss-table">
                        <thead><tr><th>Site</th><th>Adresse</th><th style={{ width: 60 }}>Action</th></tr></thead>
                        <tbody>
                          {form.sites_details.map((s, i) => (
                            <tr key={i}>
                              <td><strong>{s.nom}</strong></td>
                              <td style={{ fontSize: '0.78rem' }}>{s.adresse}</td>
                              <td><button className="gss-btn-sm" style={{ color: '#DC2626' }} onClick={() => removeSite(i)}>Retirer</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* STEP 3: Effectifs */}
              {step === 3 && (
                <>
                  {form.sites_details.length === 0 ? (
                    <p style={{ color: '#999' }}>Ajoutez des sites a l'etape 2</p>
                  ) : (
                    <div className="gss-table-wrap">
                      <table className="gss-table">
                        <thead><tr><th>Site</th><th style={{ width: 80 }}>Agents jour</th><th style={{ width: 80 }}>Agents nuit</th><th style={{ width: 80 }}>Total</th></tr></thead>
                        <tbody>
                          {form.sites_details.map((s, i) => (
                            <tr key={i}>
                              <td><strong>{s.nom}</strong></td>
                              <td><input className="gss-input" type="number" min="0" value={s.agents_jour} onChange={e => updateSite(i, 'agents_jour', parseInt(e.target.value) || 0)} style={{ width: 60, textAlign: 'center' }} /></td>
                              <td><input className="gss-input" type="number" min="0" value={s.agents_nuit} onChange={e => updateSite(i, 'agents_nuit', parseInt(e.target.value) || 0)} style={{ width: 60, textAlign: 'center' }} /></td>
                              <td style={{ fontWeight: 600 }}>{(s.agents_jour || 0) + (s.agents_nuit || 0)}</td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: 700, background: '#F5F5F5' }}>
                            <td>TOTAL</td>
                            <td>{form.sites_details.reduce((s, x) => s + (x.agents_jour || 0), 0)}</td>
                            <td>{form.sites_details.reduce((s, x) => s + (x.agents_nuit || 0), 0)}</td>
                            <td>{totalAgents}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* STEP 4: Tarification */}
              {step === 4 && (
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Tarif par agent / mois (FCFA)</label>
                    <input className="gss-input" type="number" value={form.tarif_agent} onChange={e => set('tarif_agent', parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="gss-field">
                    <label>Supervision incluse</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
                      <input type="checkbox" checked={form.supervision_incluse} onChange={e => set('supervision_incluse', e.target.checked)} /> Oui
                    </label>
                  </div>
                  <div className="gss-field gss-field-full" style={{ padding: 20, background: '#F0F4FF', borderRadius: 12, border: '2px solid #1B3A5C' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: '#666' }}>Nombre d'agents</span><strong>{totalAgents}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: '#666' }}>Tarif unitaire</span><strong>{(form.tarif_agent || 0).toLocaleString()} FCFA</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '2px solid #1B3A5C' }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#1B3A5C' }}>FORFAIT MENSUEL</span>
                      <span style={{ fontSize: 20, fontWeight: 900, color: '#1B3A5C' }}>{montantMensuel.toLocaleString()} FCFA</span>
                    </div>
                  </div>
                  <div className="gss-field gss-field-full"><label>Conditions particulieres</label>
                    <textarea className="gss-input" rows={3} value={form.conditions} onChange={e => set('conditions', e.target.value)} style={{ height: 'auto', padding: '10px 12px' }} />
                  </div>
                </div>
              )}

              {/* STEP 5: Apercu */}
              {step === 5 && (
                <div style={{ fontSize: 13 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                    <div><span style={{ color: '#999' }}>Client :</span> <strong>{clients.find(c => c.id === form.client_id)?.raison_sociale || '-'}</strong></div>
                    <div><span style={{ color: '#999' }}>Contact :</span> <strong>{form.contact_client || '-'}</strong></div>
                    <div><span style={{ color: '#999' }}>Date debut :</span> <strong>{form.date_debut || '-'}</strong></div>
                    <div><span style={{ color: '#999' }}>Duree :</span> <strong>{form.duree_mois} mois</strong></div>
                    <div><span style={{ color: '#999' }}>Sites :</span> <strong>{form.sites_details.length}</strong></div>
                    <div><span style={{ color: '#999' }}>Agents total :</span> <strong>{totalAgents}</strong></div>
                    {selectedOffre && <div><span style={{ color: '#999' }}>Offre :</span> <strong style={{ color: '#C8A84E' }}>{selectedOffre.nom}</strong></div>}
                    {selectedOffre && <div><span style={{ color: '#999' }}>Supervision :</span> <strong>{form.supervision_incluse ? 'Oui' : 'Non'}</strong></div>}
                  </div>
                  <div style={{ padding: 16, background: '#1B3A5C', borderRadius: 10, color: '#FFF', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 2 }}>Forfait mensuel</div>
                    <div style={{ fontSize: 28, fontWeight: 900 }}>{montantMensuel.toLocaleString()} FCFA</div>
                  </div>
                  {selectedOffre && Object.keys(selectedOffreServices).length > 0 && (
                    <div style={{ marginTop: 12, padding: 12, background: '#F0F4FF', borderRadius: 8, border: '1px solid #1B3A5C20' }}>
                      <strong style={{ fontSize: 12, color: '#1B3A5C' }}>Services inclus ({selectedOffre.nom}) :</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {Object.entries(selectedOffreServices).filter(([, v]) => v && v !== 'non').map(([k, v]) => (
                          <span key={k} style={{ fontSize: 11, padding: '3px 10px', background: '#1B3A5C', color: '#FFF', borderRadius: 20 }}>
                            {k.replace(/_/g, ' ')}{typeof v === 'string' && v !== 'oui' ? ` (${v})` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.sites_details.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <strong>Sites couverts :</strong>
                      {form.sites_details.map((s, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#666', marginLeft: 12 }}>{s.nom} — J:{s.agents_jour} N:{s.agents_nuit}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="gss-modal-actions">
              <button type="button" className="gss-btn-cancel" onClick={() => step > 1 ? setStep(s => s - 1) : setShowWizard(false)}>
                {step === 1 ? 'Annuler' : 'Precedent'}
              </button>
              {step < 5 ? (
                <button className="gss-btn-deploy" onClick={() => setStep(s => s + 1)}
                  disabled={step === 1 && !form.client_id}>
                  Suivant
                </button>
              ) : (
                <button className="gss-btn-deploy" onClick={handleGenerate} disabled={generating}
                  style={{ background: 'linear-gradient(135deg, #1B3A5C 0%, #0D2740 100%)' }}>
                  {generating ? 'Generation PDF...' : 'Generer le contrat PDF'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
